import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Send, Bot, User, CheckCircle, FileText, Package, ChevronDown, AlertTriangle, Link2, X, Search, Loader2, History, Clock, MessageSquare, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function Intake() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sirData, setSirData] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const scrollRef = useRef(null);
  
  // Epic selection state
  const [epics, setEpics] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [showEpicDropdown, setShowEpicDropdown] = useState(false);
  
  // Transcript upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Duplicate Detection state
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  
  // Conversation history
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Load conversation history
  const loadConversationHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/intake/chat-sessions');
      setConversationHistory(res.data);
    } catch (err) { 
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Load a past conversation
  const loadPastConversation = async (sessionId) => {
    try {
      const res = await api.get(`/intake/chat/${sessionId}`);
      setSessionId(sessionId);
      setMessages(res.data);
      setSirData(null);
      setShowHistory(false);
      
      // Check if last message has SIR data
      const lastAgentMsg = [...res.data].reverse().find(m => m.role === 'agent');
      if (lastAgentMsg) {
        const sir = parseSIR(lastAgentMsg.content);
        if (sir) setSirData(sir);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };
  
  // Quick reply options - only show when bot asks specific categorical questions
  const getQuickReplies = () => {
    if (!messages.length || messages.length < 2) return []; // Need at least one exchange
    const lastBotMessage = [...messages].reverse().find(m => m.role === 'agent');
    if (!lastBotMessage) return [];
    
    const content = lastBotMessage.content.toLowerCase();
    
    // Only show quick replies for specific follow-up questions, not the initial problem question
    // Check for urgency/priority question (usually asked after problem is described)
    if ((content.includes('urgency') || content.includes('priority') || content.includes('how urgent')) 
        && !content.includes('problem') && !content.includes('challenge')) {
      return [
        { label: 'P0 - Critical', value: 'P0 - This is critical and needs immediate attention' },
        { label: 'P1 - High', value: 'P1 - High priority but not blocking' },
        { label: 'P2 - Medium', value: 'P2 - Medium priority, can wait a bit' },
        { label: 'P3 - Low', value: 'P3 - Low priority, nice to have' }
      ];
    }
    
    // Check for "who is affected" question (specific follow-up)
    if ((content.includes('who is affected') || content.includes('which users') || content.includes('who will use') || content.includes('target users'))
        && !content.includes('problem') && !content.includes('challenge')) {
      return [
        { label: 'All platform users', value: 'All users of the platform' },
        { label: 'Internal team only', value: 'Internal team members only' },
        { label: 'Specific user group', value: 'A specific group of users' },
        { label: 'External customers', value: 'External customers and clients' }
      ];
    }
    
    // Check for success criteria question (specific follow-up)
    if ((content.includes('success criteria') || content.includes('how will you measure') || content.includes('define success') || content.includes('know when'))
        && !content.includes('problem') && !content.includes('challenge')) {
      return [
        { label: 'Faster workflow', value: 'Users can complete the task 50% faster' },
        { label: 'Fewer errors', value: 'Reduce error rate by 80%' },
        { label: 'Better satisfaction', value: 'Improved user satisfaction score' },
        { label: 'Cost reduction', value: 'Reduce operational costs by 30%' }
      ];
    }
    
    // Check for confirmation questions
    if (content.includes('does this look correct') || content.includes('is this accurate') || content.includes('confirm')) {
      return [
        { label: 'Yes, looks good', value: 'Yes, that looks correct' },
        { label: 'Make changes', value: 'I need to make some changes' }
      ];
    }
    
    return [];
  };
  
  const quickReplies = getQuickReplies();
  
  const handleQuickReply = (value) => {
    setInput(value);
  };

  useEffect(() => { 
    startNewChat(); 
    loadProducts();
    loadEpics();
  }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const loadProducts = async () => {
    try {
      const res = await api.get('/products/');
      setProducts(res.data);
    } catch (err) { console.error(err); }
  };

  const loadEpics = async () => {
    try {
      const res = await api.get('/intake/epics');
      setEpics(res.data);
    } catch (err) { console.error(err); }
  };

  const startNewChat = async () => {
    try {
      const res = await api.post('/intake/start-chat');
      setSessionId(res.data.session_id);
      setMessages([res.data.greeting]);
      setSirData(null);
    } catch (err) { console.error(err); }
  };

  const parseSIR = (text) => {
    const m = text.match(/```json\s*([\s\S]*?)```/);
    if (m) { try { const p = JSON.parse(m[1]); if (p.sir_complete) return p; } catch {} }
    return null;
  };

  const sendMessage = async () => {
    if (!input.trim() || sending || !sessionId) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { id: crypto.randomUUID(), session_id: sessionId, role: 'user', content: text, created_at: new Date().toISOString() }]);
    try {
      const res = await api.post('/intake/chat', { session_id: sessionId, content: text });
      setMessages(prev => [...prev, res.data]);
      const sir = parseSIR(res.data.content);
      if (sir) setSirData(sir);
    } catch {
      setMessages(prev => [...prev, { id: 'err', session_id: sessionId, role: 'agent', content: 'Sorry, I encountered an issue. Please try again.', created_at: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const handleTranscriptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;

    const validTypes = ['.pdf', '.docx', '.txt'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validTypes.includes(ext)) { alert('Please upload a PDF, DOCX, or TXT file.'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('File must be under 10MB.'); return; }

    setUploading(true);
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(), session_id: sessionId, role: 'user',
      content: `\u{1F4CE} Uploaded meeting transcript: ${file.name}`,
      created_at: new Date().toISOString()
    }]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);
      const res = await api.post('/intake/upload-transcript', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, res.data]);
      const sir = parseSIR(res.data.content);
      if (sir) setSirData(sir);
    } catch {
      setMessages(prev => [...prev, {
        id: 'err-upload', session_id: sessionId, role: 'agent',
        content: 'Sorry, I had trouble processing the transcript. Please try again or paste the text directly.',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submitSIR = async () => {
    if (!sirData) return;
    try {
      await api.post('/intake/items', { 
        title: sirData.title || 'Untitled', 
        problem: sirData.problem || '', 
        users_affected: sirData.users_affected || '', 
        success_criteria: sirData.success_criteria || '', 
        product: sirData.product || selectedProduct?.name || '', 
        product_id: selectedProduct?.id || null,
        urgency: sirData.urgency || 'P1', 
        tags: sirData.tags || [],
        parent_id: selectedEpic?.id || null,  // Link to parent Epic if selected
        initiative_type: selectedEpic ? 'feature' : null  // Mark as feature if under an Epic
      });
      setSirData(null);
      setSelectedProduct(null);
      setSelectedEpic(null);
      setDuplicateResults(null);
      startNewChat();
    } catch (err) { console.error(err); }
  };

  // Check for duplicates before submission
  const checkForDuplicates = async () => {
    if (!sirData) return;
    setCheckingDuplicates(true);
    setDuplicateResults(null);
    try {
      const res = await api.post('/agents/duplicates/check', {
        title: sirData.title,
        problem: sirData.problem,
        product_id: selectedProduct?.id || null
      });
      if (res.data.success) {
        setDuplicateResults(res.data);
        if (res.data.has_duplicates) {
          setShowDuplicateModal(true);
        } else {
          // No duplicates, proceed with submission
          submitSIR();
        }
      } else {
        console.error('Duplicate check failed:', res.data.error);
        // Proceed anyway on error
        submitSIR();
      }
    } catch (err) {
      console.error(err);
      // Proceed anyway on error
      submitSIR();
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const clean = (text) => text.replace(/```json[\s\S]*?```/g, '').trim();

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col relative" data-testid="intake-page">
      <div className="px-4 lg:px-8 py-4 border-b border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-[#0f0f23] flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#f0dcf0] dark:bg-[#98139C]/20 rounded-full flex items-center justify-center">
              <Bot size={20} className="text-[#98139C] dark:text-[#d87bdb]" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-lg font-heading text-[#30302F] dark:text-white">Intake Agent</h1>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Active
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              data-testid="history-btn" 
              onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadConversationHistory(); }}
              className="border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/5 h-9 rounded-full font-bold text-sm px-4 transition-colors flex items-center gap-2"
            >
              <History size={14} aria-hidden="true" /> History
            </button>
            <button data-testid="new-conversation-btn" onClick={startNewChat} className="border-2 border-[#4F0077] dark:border-[#98139C] text-[#4F0077] dark:text-[#d87bdb] hover:bg-[#f9f5fa] dark:hover:bg-white/5 h-9 rounded-full font-bold text-sm px-5 transition-colors">
              New Conversation
            </button>
          </div>
        </div>
      </div>

      {/* Conversation History Panel */}
      {showHistory && (
        <div className="absolute right-4 top-20 z-50 w-80 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 shadow-xl max-h-96 overflow-y-auto" data-testid="history-panel">
          <div className="p-3 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between sticky top-0 bg-white dark:bg-[#1a1a2e]">
            <h3 className="text-sm font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <History size={14} className="text-[#98139C]" /> Past Conversations
            </h3>
            <button onClick={() => setShowHistory(false)} className="text-[#30302F]/70 dark:text-white/70 hover:text-[#30302F] dark:hover:text-white" aria-label="Close history panel">
              <X size={16} />
            </button>
          </div>
          
          {loadingHistory ? (
            <div className="p-4 text-center text-sm text-[#30302F]/70 dark:text-white/70">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" />
              Loading history...
            </div>
          ) : conversationHistory.length === 0 ? (
            <div className="p-4 text-center text-sm text-[#30302F]/70 dark:text-white/70">
              <MessageSquare size={24} className="mx-auto mb-2 opacity-50" />
              No past conversations
            </div>
          ) : (
            <div className="divide-y divide-[#e3e3e3] dark:divide-white/10">
              {conversationHistory.map((conv) => (
                <button
                  key={conv.session_id}
                  onClick={() => loadPastConversation(conv.session_id)}
                  className="w-full p-3 text-left hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
                  data-testid={`history-item-${conv.session_id}`}
                >
                  <p className="text-sm text-[#30302F] dark:text-white line-clamp-2">{conv.preview}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-[#30302F]/70 dark:text-white/70">
                    <Clock size={10} />
                    <span>{new Date(conv.created_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{conv.message_count} messages</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 lg:p-8 space-y-4 chat-scroll bg-[#faf8fb] dark:bg-[#0f0f23]" data-testid="chat-messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex gap-3 max-w-[75%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-[#4F0077] text-white' : 'bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#98139C] dark:text-[#d87bdb]'}`}>
                {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
              </div>
              <div data-testid={`chat-message-${msg.role}`} className={`px-4 py-3 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-[#4F0077] text-white' : 'bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white'}`}>
                {msg.role === 'agent' ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({children}) => <p className="my-1 text-[#30302F] dark:text-white">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-4 my-1 text-[#30302F] dark:text-white">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-4 my-1 text-[#30302F] dark:text-white">{children}</ol>,
                        li: ({children}) => <li className="my-0.5">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-[#4F0077] dark:text-[#d87bdb]">{children}</strong>,
                        em: ({children}) => <em className="italic">{children}</em>,
                        code: ({children}) => <code className="bg-[#f0dcf0] dark:bg-[#98139C]/20 px-1 py-0.5 text-xs rounded font-mono">{children}</code>,
                        h1: ({children}) => <h1 className="text-lg font-bold text-[#30302F] dark:text-white my-2">{children}</h1>,
                        h2: ({children}) => <h2 className="text-base font-bold text-[#30302F] dark:text-white my-2">{children}</h2>,
                        h3: ({children}) => <h3 className="text-sm font-bold text-[#30302F] dark:text-white my-1">{children}</h3>,
                        a: ({href, children}) => <a href={href} className="text-[#98139C] hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                      }}
                    >
                      {clean(msg.content)}
                    </ReactMarkdown>
                  </div>
                ) : (
                  clean(msg.content).split('\n').map((line, i) => <p key={i} className={i > 0 ? 'mt-2' : ''}>{line}</p>)
                )}
              </div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 flex items-center justify-center"><Bot size={14} className="text-[#98139C] dark:text-[#d87bdb]" /></div>
              <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 px-4 py-3"><div className="flex gap-1"><span className="w-2 h-2 bg-[#98139C]/30 rounded-full typing-dot" /><span className="w-2 h-2 bg-[#98139C]/30 rounded-full typing-dot" /><span className="w-2 h-2 bg-[#98139C]/30 rounded-full typing-dot" /></div></div>
            </div>
          </div>
        )}
        {sirData && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 mx-auto max-w-lg" data-testid="sir-preview-card">
            <div className="flex items-center gap-2 mb-4"><FileText size={18} className="text-[#98139C]" /><h3 className="font-heading text-[#30302F] dark:text-white">Structured Intake Record</h3></div>
            <div className="space-y-3 text-sm">
              <div><span className="font-medium text-[#30302F] dark:text-white">Title:</span> <span className="text-[#30302F] dark:text-white">{sirData.title}</span></div>
              <div><span className="font-medium text-[#30302F] dark:text-white">Problem:</span> <span className="text-[#30302F] dark:text-white font-light">{sirData.problem}</span></div>
              <div><span className="font-medium text-[#30302F] dark:text-white">Users:</span> <span className="text-[#30302F] dark:text-white font-light">{sirData.users_affected}</span></div>
              <div><span className="font-medium text-[#30302F] dark:text-white">Success Criteria:</span> <span className="text-[#30302F] dark:text-white font-light">{sirData.success_criteria}</span></div>
              
              {/* Product Selection */}
              <div className="relative">
                <span className="font-medium text-[#30302F] dark:text-white">Link to Product:</span>
                <button
                  type="button"
                  onClick={() => setShowProductDropdown(!showProductDropdown)}
                  className="w-full mt-1 h-10 px-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-left text-sm flex items-center justify-between hover:border-[#98139C] transition-colors"
                  data-testid="product-select-btn"
                  aria-label="Select product"
                >
                  {selectedProduct ? (
                    <span className="flex items-center gap-2 text-[#30302F] dark:text-white">
                      <Package size={14} className="text-[#98139C]" /> {selectedProduct.name}
                    </span>
                  ) : (
                    <span className="text-[#30302F] dark:text-white">Select product (optional)</span>
                  )}
                  <ChevronDown size={14} className="text-[#30302F] dark:text-white" />
                </button>
                {showProductDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 shadow-lg max-h-48 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { setSelectedProduct(null); setShowProductDropdown(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/5"
                    >
                      No product
                    </button>
                    {products.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setSelectedProduct(p); setShowProductDropdown(false); }}
                        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#f9f5fa] dark:hover:bg-white/5"
                        data-testid={`product-option-${p.id}`}
                      >
                        <Package size={14} className="text-[#98139C]" />
                        <span className="text-[#30302F] dark:text-white">{p.name}</span>
                        <Badge className="text-[10px] ml-auto bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] border-0">{p.status}</Badge>
                      </button>
                    ))}
                    {products.length === 0 && (
                      <div className="px-3 py-2 text-sm text-[#30302F] dark:text-white">No products available</div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Epic Selection - Link to parent initiative */}
              <div className="relative">
                <span className="font-medium text-[#30302F] dark:text-white">Link to Epic/Initiative:</span>
                <button
                  type="button"
                  onClick={() => setShowEpicDropdown(!showEpicDropdown)}
                  className="w-full mt-1 h-10 px-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-left text-sm flex items-center justify-between hover:border-[#98139C] transition-colors"
                  data-testid="epic-select-btn"
                >
                  {selectedEpic ? (
                    <span className="flex items-center gap-2 text-[#30302F] dark:text-white">
                      <Link2 size={14} className="text-[#98139C]" /> {selectedEpic.title}
                    </span>
                  ) : (
                    <span className="text-[#30302F] dark:text-white">No parent (standalone item)</span>
                  )}
                  <ChevronDown size={14} className="text-[#30302F] dark:text-white" />
                </button>
                {showEpicDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 shadow-lg max-h-48 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { setSelectedEpic(null); setShowEpicDropdown(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/5"
                    >
                      No parent (standalone)
                    </button>
                    {epics.map(e => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => { setSelectedEpic(e); setShowEpicDropdown(false); }}
                        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#f9f5fa] dark:hover:bg-white/5"
                        data-testid={`epic-option-${e.id}`}
                      >
                        <Link2 size={14} className="text-[#98139C]" />
                        <span className="text-[#30302F] dark:text-white truncate">{e.title}</span>
                        {e.product && (
                          <span className="text-[11px] ml-auto text-[#30302F]/70 dark:text-white/70">{e.product}</span>
                        )}
                      </button>
                    ))}
                    {epics.length === 0 && (
                      <div className="px-3 py-2 text-sm text-[#30302F] dark:text-white">No epics available</div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Badge className="bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] border-0 rounded-none">{sirData.urgency}</Badge>
                {sirData.tags?.map(t => <Badge key={t} variant="outline" className="text-xs rounded-none dark:border-white/10 dark:text-white">{t}</Badge>)}
                {selectedEpic && (
                  <Badge className="bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-0 rounded-none text-[10px]">
                    Feature
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button 
                data-testid="confirm-sir-btn" 
                onClick={checkForDuplicates} 
                disabled={checkingDuplicates}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60"
              >
                {checkingDuplicates ? (
                  <><Loader2 size={16} className="animate-spin" /> Checking...</>
                ) : (
                  <><CheckCircle size={16} /> Confirm & Submit</>
                )}
              </button>
              <button data-testid="edit-sir-btn" onClick={() => setSirData(null)} className="border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold text-sm px-6 py-2.5 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors">Edit</button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Reply Buttons */}
      {quickReplies.length > 0 && !sirData && (
        <div className="px-4 lg:px-8 py-3 bg-[#faf8fb] dark:bg-[#0f0f23] border-t border-[#e3e3e3] dark:border-white/10 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F]/70 dark:text-white/70 mb-2">Quick Replies</p>
            <div className="flex flex-wrap gap-2">
              {quickReplies.map((reply, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickReply(reply.value)}
                  className="px-3 py-1.5 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:border-[#98139C] hover:bg-[#f9f5fa] dark:hover:bg-white/10 transition-colors rounded-full"
                  data-testid={`quick-reply-${i}`}
                >
                  {reply.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 lg:px-8 py-4 border-t border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-[#0f0f23] flex-shrink-0">
        <div className="flex gap-3 max-w-4xl mx-auto">
          <input type="file" ref={fileInputRef} onChange={handleTranscriptUpload} accept=".pdf,.docx,.txt" className="hidden" />
          <button onClick={() => fileInputRef.current?.click()} disabled={sending || uploading || !sessionId} className="h-11 w-11 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 rounded-full flex items-center justify-center text-[#30302F] dark:text-white hover:border-[#98139C] hover:text-[#98139C] transition-colors disabled:opacity-40" title="Upload meeting transcript" data-testid="upload-transcript-btn">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
          </button>
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()} placeholder="Describe your idea or answer the agent's question..." className="flex-1 h-11 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 dark:text-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#98139C] focus:border-transparent font-light" disabled={sending} />
          <button data-testid="send-message-btn" onClick={sendMessage} disabled={sending || !input.trim()} className="bg-[#98139C] hover:bg-[#7a0f7d] text-white h-11 w-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-40" aria-label="Send message"><Send size={16} /></button>
        </div>
      </div>

      {/* Duplicate Detection Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              Potential Duplicates Detected
            </DialogTitle>
          </DialogHeader>
          
          {duplicateResults && (
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 p-4">
                <p className="text-sm text-amber-800 dark:text-amber-300">{duplicateResults.summary}</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Highest similarity: <strong>{duplicateResults.highest_similarity}%</strong>
                </p>
              </div>

              {/* New Item Preview */}
              <div className="border border-[#e3e3e3] dark:border-white/10 p-4">
                <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C] mb-2">Your New Item</p>
                <h4 className="font-bold text-[#30302F] dark:text-white">{sirData?.title}</h4>
                <p className="text-sm text-[#30302F] dark:text-white mt-1 line-clamp-2">{sirData?.problem}</p>
              </div>

              {/* Matches */}
              {duplicateResults.matches?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-3">
                    Similar Existing Items ({duplicateResults.matches.length})
                  </p>
                  <div className="space-y-3">
                    {duplicateResults.matches.map((match, idx) => (
                      <div 
                        key={idx}
                        className="border border-[#e3e3e3] dark:border-white/10 p-4 hover:border-[#98139C] transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <h5 className="font-bold text-[#30302F] dark:text-white text-sm">
                              {match.existing_item_title}
                            </h5>
                            {match.overlapping_aspects?.length > 0 && (
                              <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-1">
                                Overlaps: {match.overlapping_aspects.join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={`text-xs ${
                              match.similarity_score >= 80 ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' :
                              match.similarity_score >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                              'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                            }`}>
                              {match.similarity_score}% match
                            </Badge>
                            <Badge className="text-[10px] bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/80">
                              {match.match_type?.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Recommendation */}
                        <div className="mt-3 pt-3 border-t border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
                          <p className="text-xs text-[#30302F] dark:text-white">
                            <strong>Recommendation:</strong> {match.recommendation?.replace('_', ' ')}
                          </p>
                          <a 
                            href={`/backlog/${match.existing_item_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#98139C] hover:underline flex items-center gap-1"
                          >
                            View Item <Link2 size={10} />
                          </a>
                        </div>
                        
                        {/* Reasoning */}
                        {match.reasoning && (
                          <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-2 italic">
                            {match.reasoning}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                <button
                  onClick={() => {
                    setShowDuplicateModal(false);
                    submitSIR();
                  }}
                  className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors"
                  data-testid="submit-anyway-btn"
                >
                  <CheckCircle size={16} /> Submit Anyway
                </button>
                <button
                  onClick={() => {
                    setShowDuplicateModal(false);
                    setSirData(null);
                  }}
                  className="flex-1 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-11 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
                  data-testid="cancel-duplicate-btn"
                >
                  Cancel & Review
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
